import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { BlindBaselinePanel } from "@/components/BlindBaselinePanel";
import { DeltaP03Panel } from "@/components/DeltaP03Panel";
import { SpikeP03HPanel, DecisionTableP03H } from "@/components/SpikeP03HPanel";
import { P04Panel } from "@/components/P04Panel";

const PHASE_A_FIELDS = [
  ["componenti", "Componenti (elenco)"], ["quantita", "Quantità"],
  ["materiale", "Materiale"], ["spessore_mm", "Spessore (mm)"],
  ["dimensioni", "Dimensioni finite"], ["fori_asole", "Fori e asole (Ø, qty, posizioni)"],
  ["contorni", "Contorni interni/esterni"], ["pieghe", "Pieghe (n°, angoli, raggi)"],
  ["saldature", "Saldature (tipo, lunghezze)"], ["tolleranze", "Tolleranze"],
  ["trattamenti", "Trattamenti"], ["mancanze_conflitti", "Informazioni mancanti e conflitti"],
];
const PHASE_FIELDS = {
  B: [["tipo_componente", "Tipo componente"], ["grezzo", "Grezzo corretto"], ["lavorazioni", "Lavorazioni necessarie"]],
  C: [["operazioni_sequenza", "Operazioni e sequenza"], ["macchine", "Macchine"], ["setup_tempi", "Setup e tempi"], ["lavorazioni_esterne", "Lavorazioni esterne"]],
  D: [["quantita", "Quantità"], ["tempi", "Tempi approvati"], ["costi", "Costi"], ["prezzo", "Prezzo"], ["consuntivo", "Consuntivo reale (se disponibile)"]],
};

const PhaseForm = ({ phase, label, data, editable, onSave, fields }) => {
  const [payload, setPayload] = useState(data?.payload || {});
  useEffect(() => { setPayload(data?.payload || {}); }, [data]);
  const locked = data?.state === "locked";
  return (
    <Card data-testid={`phase-card-${phase}`}>
      <CardHeader className="py-3">
        <CardTitle className="text-sm flex items-center gap-2">
          FASE {phase} — {label}
          <Badge className={`text-[10px] ${locked ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}
            data-testid={`phase-state-${phase}`}>{data?.state || "empty"}</Badge>
          {locked && data?.locked_by && <span className="text-[10px] text-muted-foreground">bloccata da {data.locked_by} · {data.locked_at}</span>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {fields.map(([k, lbl]) => (
          <div key={k}>
            <label className="text-xs text-muted-foreground">{lbl}</label>
            <Textarea className="text-sm min-h-[36px]" value={payload[k] || ""} disabled={locked || !editable}
              data-testid={`phase-${phase}-field-${k}`}
              onChange={(e) => setPayload({ ...payload, [k]: e.target.value })} />
          </div>
        ))}
        {!locked && editable && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" data-testid={`phase-${phase}-save-btn`}
              onClick={() => onSave(phase, payload, false)}>Salva bozza</Button>
            <Button size="sm" data-testid={`phase-${phase}-lock-btn`}
              onClick={() => { if (window.confirm(`Bloccare la FASE ${phase}?${phase === "A" ? " La ground truth cieca diventa IMMUTABILE e verrà mostrato il risultato QuoteOS." : ""}`)) onSave(phase, payload, true); }}>
              <Lock size={12} className="mr-1" /> Blocca
            </Button>
          </div>
        )}
        {!editable && !locked && <p className="text-xs text-red-700">Disponibile solo dopo il blocco della FASE A.</p>}
      </CardContent>
    </Card>
  );
};

export default function CalibrationCase() {
  const { caseId } = useParams();
  const [data, setData] = useState(null);
  const [result, setResult] = useState(null);
  const [diffForm, setDiffForm] = useState({ category: "FEATURE_MISSING", quoteos_value: "", technician_value: "", motivation: "", evidence: "" });

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/calibration/cases/${caseId}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const savePhase = async (phase, payload, lock) => {
    try {
      await api.post(`/calibration/cases/${caseId}/phases/${phase}`, { payload, lock });
      toast.success(`FASE ${phase} ${lock ? "bloccata" : "salvata"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
  };
  const loadResult = async () => {
    try {
      const r = await api.get(`/calibration/cases/${caseId}/quoteos-result`, { timeout: 240000 });
      setResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Risultato non disponibile"); }
  };
  const sendDiff = async () => {
    try {
      await api.post(`/calibration/cases/${caseId}/differences`, diffForm);
      toast.success("Differenza registrata");
      setDiffForm({ ...diffForm, quoteos_value: "", technician_value: "", motivation: "", evidence: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  if (!data) return <div className="p-8 text-sm" data-testid="case-loading">Caricamento caso…</div>;
  const aLocked = data.phases.A.state === "locked";

  return (
    <div className="p-4 space-y-4 max-w-6xl" data-testid="calibration-case-page">
      <div className="flex items-center gap-3">
        <Link to="/calibration" className="text-sm flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="case-back-link">
          <ArrowLeft size={14} /> Calibrazione
        </Link>
        <h1 className="text-lg font-semibold">{data.case_id} — {data.pdf_name}</h1>
      </div>

      {!aLocked && (
        <div className="p-3 text-sm border bg-sky-50 border-sky-300 text-sky-900" data-testid="blind-phase-banner">
          <strong>FASE A CIECA</strong>: guarda SOLO il disegno qui sotto e compila la ground truth.
          Il risultato QuoteOS resterà nascosto finché non blocchi la FASE A.
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card data-testid="case-pdf-card">
          <CardHeader className="py-3"><CardTitle className="text-sm">Disegno originale</CardTitle></CardHeader>
          <CardContent>
            <iframe title="pdf" src={`${api.defaults.baseURL}/calibration/cases/${caseId}/pdf`}
              className="w-full h-[560px] border border-border" data-testid="case-pdf-frame" />
          </CardContent>
        </Card>

        <div className="space-y-4">
          <PhaseForm phase="A" label={data.phase_labels.A} data={data.phases.A} editable onSave={savePhase} fields={PHASE_A_FIELDS} />
        </div>
      </div>

      {aLocked && (
        <>
          <BlindBaselinePanel caseId={caseId} gtPayload={data.phases.A.payload} />

          <DeltaP03Panel caseId={caseId} />

          {["PDF-017", "PDF-023"].includes(caseId) && (
            <>
              <SpikeP03HPanel caseId={caseId} />
              <DecisionTableP03H />
              <P04Panel caseId={caseId} />
            </>
          )}

          <Card data-testid="quoteos-result-card">
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                Risultato QuoteOS (visibile solo dopo il blocco della FASE A)
                <Button size="sm" variant="outline" onClick={loadResult} data-testid="load-quoteos-result-btn">Carica risultato</Button>
              </CardTitle>
            </CardHeader>
            {result && (
              <CardContent>
                <pre className="text-xs whitespace-pre-wrap font-sans" data-testid="quoteos-result-explanation">{result.explanation}</pre>
                <p className="text-xs mt-2">Stato prezzo: <strong>{result.quote.price_status}</strong> · profilo v{result.profile_version}</p>
              </CardContent>
            )}
          </Card>

          <div className="grid lg:grid-cols-3 gap-4">
            {["B", "C", "D"].map((ph) => (
              <PhaseForm key={ph} phase={ph} label={data.phase_labels[ph]} data={data.phases[ph]} editable onSave={savePhase} fields={PHASE_FIELDS[ph]} />
            ))}
          </div>

          <Card data-testid="differences-card">
            <CardHeader className="py-3"><CardTitle className="text-sm">Differenze QuoteOS ↔ tecnico ({data.differences.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <div className="grid md:grid-cols-2 gap-2">
                <select className="border border-border text-sm p-1.5 bg-background" value={diffForm.category}
                  data-testid="diff-category-select"
                  onChange={(e) => setDiffForm({ ...diffForm, category: e.target.value })}>
                  {["FEATURE_MISSING", "FALSE_POSITIVE", "DUPLICATE_VIEW_FEATURE", "WRONG_VALUE", "WRONG_DIMENSION_ASSOCIATION", "WRONG_MATERIAL", "WRONG_QUANTITY", "MISSING_OPERATION", "UNNECESSARY_OPERATION", "WRONG_SEQUENCE", "WRONG_MACHINE", "WRONG_TIME", "WRONG_COST", "NECESSARY_BLOCK", "FALSE_BLOCK", "DANGEROUS_UNBLOCKED_VALUE", "DOCUMENT_AMBIGUITY"].map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <Input placeholder="Evidenza sul disegno (vista/quota/nota)" value={diffForm.evidence}
                  data-testid="diff-evidence-input"
                  onChange={(e) => setDiffForm({ ...diffForm, evidence: e.target.value })} />
                <Input placeholder="Valore QuoteOS" value={diffForm.quoteos_value}
                  data-testid="diff-quoteos-input"
                  onChange={(e) => setDiffForm({ ...diffForm, quoteos_value: e.target.value })} />
                <Input placeholder="Valore corretto del tecnico" value={diffForm.technician_value}
                  data-testid="diff-technician-input"
                  onChange={(e) => setDiffForm({ ...diffForm, technician_value: e.target.value })} />
              </div>
              <Textarea placeholder="Motivazione tecnica (obbligatoria)" value={diffForm.motivation}
                data-testid="diff-motivation-input"
                onChange={(e) => setDiffForm({ ...diffForm, motivation: e.target.value })} />
              <Button size="sm" onClick={sendDiff} data-testid="diff-submit-btn">Registra differenza</Button>
              <div className="space-y-1 max-h-60 overflow-auto">
                {data.differences.map((d, i) => (
                  <div key={i} className="text-xs border-b border-border py-1" data-testid={`diff-row-${i}`}>
                    <Badge className="bg-zinc-100 text-zinc-700 text-[10px] mr-1">{d.category}</Badge>
                    QuoteOS: {d.quoteos_value || "—"} → tecnico: {d.technician_value || "—"} — {d.motivation}
                    <span className="text-muted-foreground"> · {d.user_email} · {d.at}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
